// ===== BEGIN FILE: src/components/FloatingChartModal.tsx =====
/**
 * Floating Chart Modal - Draggable and resizable chart viewer
 * Admin-only feature for keeping chart visible while navigating the app
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/joy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CloseIcon from '@mui/icons-material/Close';
import AdvancedNorthIndianChart from './AdvancedNorthIndianChart';
import PlanetDetailsSidePanel from './PlanetDetailsSidePanel';
import HouseDetailsSidePanel from './HouseDetailsSidePanel';
import AudioGuide from './AudioGuide';

type PlanetName = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';

interface FloatingChartModalProps {
  open: boolean;
  onClose: () => void;
  chartData: any; // Same structure as AdvancedNorthIndianChart expects
  personName?: string;
  houseScores?: Record<number, number>; // House total scores (1-12)
  houseInterpretations?: import('../utils/fetchHouseInterpretations').HouseInterpretation[];
  bhavaBalaRupas?: Record<number, number>;
}

export default function FloatingChartModal({
  open,
  onClose,
  chartData,
  personName,
  houseScores,
  houseInterpretations,
  bhavaBalaRupas,
}: FloatingChartModalProps) {
  const [selectedPlanet, setSelectedPlanet] = useState<PlanetName | null>(null);
  const [selectedHouse, setSelectedHouse] = useState<number | null>(null);
  const [activeChartCue, setActiveChartCue] = useState<string | null>(null);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [size, setSize] = useState({ width: 700, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const modalRef = useRef<HTMLDivElement>(null);

  // Chart audio guide cue handler (same timestamps as App.tsx interactive tab)
  const CHART_AUDIO_CUES = [
    { from:  4.5, to: 18.0, fieldCue: 'chart-house-header' },
    { from: 18.0, to: 27.0, fieldCue: 'chart-house-signification' },
    { from: 27.0, to: 44.0, fieldCue: 'chart-planet-impact' },
    { from: 44.0, to: 64.0, fieldCue: 'chart-house-lord' },
    { from: 64.0, to: 74.0, fieldCue: 'chart-lord-hint' },
  ] as const;

  const handleChartAudioTime = (t: number) => {
    let found: string | null = null;
    for (const cue of CHART_AUDIO_CUES) {
      if (t >= cue.from && t < cue.to) found = cue.fieldCue;
    }
    setActiveChartCue(found);
  };

  useEffect(() => {
    document.querySelectorAll('.audio-cue-active').forEach(el => el.classList.remove('audio-cue-active'));
    if (activeChartCue) {
      const el = document.querySelector(`[data-audio-cue="${activeChartCue}"]`);
      if (el) {
        el.classList.add('audio-cue-active');
        const headerEl = document.querySelector('[data-audio-guide-header]') as HTMLElement | null;
        const headerHeight = headerEl ? headerEl.offsetHeight : 100;
        const rect = el.getBoundingClientRect();
        window.scrollTo({ top: rect.top + window.scrollY - headerHeight - 8, behavior: 'smooth' });
      }
    }
  }, [activeChartCue]);

  // Extract selected planet data once instead of repeated find calls
  const selectedPlanetData = useMemo(() => {
    if (!selectedPlanet || !chartData?.planets) return null;
    return chartData.planets.find((p: any) => p.name === selectedPlanet);
  }, [selectedPlanet, chartData]);

  // Handle dragging
  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.modal-close-button')) return; // Don't drag when clicking close
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Handle resizing
  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    setSize({
      width: Math.max(500, resizeStart.width + deltaX),
      height: Math.max(400, resizeStart.height + deltaY)
    });
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  // Attach/detach event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, resizeStart]);

  // Reset position and size when modal opens
  useEffect(() => {
    if (open) {
      const modalWidth = 700;
      const modalHeight = 500;
      setPosition({ x: 0, y: 0 }); // Start centered (transform handles it)
      setSize({ width: modalWidth, height: modalHeight });
      setSelectedPlanet(null);
    }
  }, [open]);

  if (!open) return null;

  return (
    <Box
      ref={modalRef}
      sx={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        width: size.width,
        height: size.height,
        zIndex: 10005,
        borderRadius: 'lg',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        bgcolor: 'background.surface',
        display: 'flex',
        flexDirection: 'column',
        cursor: isDragging ? 'grabbing' : 'default',
        overflow: 'hidden',
      }}
    >
        {/* Draggable Header */}
        <Box
          onMouseDown={handleDragStart}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicatorIcon sx={{ fontSize: '1.2rem', opacity: 0.8 }} />
            <Typography level="title-md" sx={{ fontWeight: 600, color: 'white' }}>
              {personName ? `${personName}'s Chart` : 'Interactive Chart'}
            </Typography>
            <AudioGuide
              src="/audio/guide-interactive-chart.mp3"
              label=""
              size="sm"
              variant="red"
              onTimeUpdate={handleChartAudioTime}
            />
          </Box>
          <IconButton
            onClick={onClose}
            variant="plain"
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Chart Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Main Chart */}
          <Box
            sx={{
              flex: (selectedPlanet || selectedHouse) ? '0 0 70%' : 1,
              overflow: 'auto',
              pl: 2,
              pr: 0,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end', // Push chart to the right edge
            }}
          >
            <AdvancedNorthIndianChart
              chartData={chartData}
              personName={personName}
              size={Math.min(size.width * 0.6, size.height * 0.8)}
              showExport={false}
              onPlanetSelect={(planet) => {
                setSelectedPlanet(planet);
                if (planet) setSelectedHouse(null); // Clear house selection when planet is selected
              }}
              selectedPlanet={selectedPlanet}
              onHouseSelect={(house) => {
                setSelectedHouse(house);
                if (house) setSelectedPlanet(null); // Clear planet selection when house is selected
              }}
            />
          </Box>

          {/* Side Panel - Planet Details */}
          {selectedPlanet && selectedPlanetData && chartData && (
            <Box
              sx={{
                flex: '0 0 30%',
                overflow: 'auto',
                fontSize: '0.65rem',
                p: 0,
                m: 0,
                transform: 'scale(0.8)',
                transformOrigin: 'top left',
                width: '125%', // Compensate for scale (1/0.8)
                '& .MuiTypography-root': {
                  fontSize: '0.65rem !important',
                  lineHeight: '1.3 !important',
                },
                '& .MuiChip-root': {
                  fontSize: '0.55rem !important',
                  height: '18px !important',
                  minHeight: '18px !important',
                  px: '6px !important',
                },
                '& .MuiSheet-root': {
                  p: '6px !important',
                },
              }}
            >
              <PlanetDetailsSidePanel
                planetData={{
                  name: selectedPlanet,
                  house: selectedPlanetData.house || 1,
                  rasi: selectedPlanetData.rasi || 1,
                  degree: selectedPlanetData.degree || 0,
                  nakshatra: selectedPlanetData.nakshatra,
                  nakshatraPada: selectedPlanetData.nakshatraPada,
                  retro: selectedPlanetData.retro || false,
                  combust: selectedPlanetData.combust || false,
                  exalted: selectedPlanetData.exalted || false,
                  debilitated: selectedPlanetData.debilitated || false,
                  blendedPct: selectedPlanetData.blendedPct,
                }}
                aspects={chartData.aspects}
                lagnaRashi={chartData.ascendant.rasi}
                getPlanetColor={(planet) => {
                  // Simple color based on functional role - you can enhance this
                  return '#667eea';
                }}
                compact={true}
              />
            </Box>
          )}

          {/* Side Panel - House Details */}
          {selectedHouse && chartData && (() => {
            // Calculate sign number for selected house
            const signNumber = ((chartData.ascendant.rasi - 1 + (selectedHouse - 1)) % 12) + 1;
            
            // Sign lord mapping
            const SIGN_LORD: Record<number, PlanetName> = {
              1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
              7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
            };
            
            const lordName = SIGN_LORD[signNumber];
            
            // Find where the lord is sitting
            const lordPlanetData = chartData.planets?.find((p: any) => p.name === lordName);
            const lordHouse = lordPlanetData?.house || 1;
            
            // Get house score
            const houseScore = houseScores?.[selectedHouse];
            
            return (
              <Box
                sx={{
                  flex: '0 0 30%',
                  overflow: 'auto',
                  fontSize: '0.65rem',
                  p: 0,
                  m: 0,
                  transform: 'scale(0.8)',
                  transformOrigin: 'top left',
                  width: '125%', // Compensate for scale (1/0.8)
                  '& .MuiTypography-root': {
                    fontSize: '0.65rem !important',
                    lineHeight: '1.3 !important',
                  },
                  '& .MuiChip-root': {
                    fontSize: '0.55rem !important',
                    height: '18px !important',
                    minHeight: '18px !important',
                    px: '6px !important',
                  },
                }}
              >
                <HouseDetailsSidePanel
                  houseNumber={selectedHouse}
                  signNumber={signNumber}
                  houseScore={houseScore}
                  lordName={lordName}
                  lordHouse={lordHouse}
                  houseInterpretations={houseInterpretations}
                  bhavaBalaRupas={bhavaBalaRupas}
                />
              </Box>
            );
          })()}
        </Box>

        {/* Resize Handle */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 24,
            height: 24,
            cursor: 'nwse-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.tertiary',
            bgcolor: 'background.level1',
            borderTopLeftRadius: 'sm',
            zIndex: 1,
            '&:hover': {
              color: 'primary.500',
              bgcolor: 'primary.50',
            }
          }}
        >
          <AspectRatioIcon sx={{ fontSize: '1.1rem', transform: 'rotate(90deg)' }} />
        </Box>

        {/* Resize Instructions */}
        <Tooltip
          title="Drag from header to move • Drag bottom-right corner to resize"
          placement="top"
          arrow
          variant="soft"
          size="sm"
          sx={{ position: 'absolute', bottom: 8, left: 8, pointerEvents: 'none' }}
        >
          <Typography level="body-xs" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
            💡 Floating Chart
          </Typography>
        </Tooltip>
      </Box>
  );
}
